import { z } from "zod/v4";

export const eventTypeSchema = z.enum(["training", "camp", "match", "meeting", "other"]);

export const eventStatusSchema = z.enum(["scheduled", "cancelled", "completed"]);

export const attendeeStatusSchema = z.enum(["going", "maybe", "not_going"]);

// Who an event is meant for. Not the RSVP: visibility decides who may see and
// join at all, attendee status is what a person answered.
export const eventVisibilitySchema = z.enum(["public", "invite_only"]);

// The viewer's own standing on an invite-only event. Null when they hold no
// invitation, which for a public event is the normal case.
export const inviteStatusSchema = z.enum(["invited", "accepted", "declined", "revoked", "expired"]);

export const eventAttendeeSchema = z.object({
	id: z.string().uuid(),
	event_id: z.string().uuid(),
	user_id: z.string().uuid(),
	status: attendeeStatusSchema,
	display_name: z.string(),
	avatar_url: z.string().nullable().optional(),
	checked_in_at: z.coerce.date().nullable().optional(),
	waitlist_status: z.enum(["confirmed", "waitlisted"]).optional(),
	created_at: z.coerce.date(),
});

export const eventSchema = z.object({
	id: z.string().uuid(),
	club_id: z.string().uuid(),
	creator_id: z.string().uuid(),
	title: z.string(),
	description: z.string().nullable().optional(),
	event_type: eventTypeSchema,
	status: eventStatusSchema,
	start_time: z.coerce.date(),
	end_time: z.coerce.date(),
	location: z.string().nullable().optional(),
	max_attendees: z.number().int().nullable().optional(),
	is_recurring: z.boolean(),
	cover_image_url: z.string().nullable().optional(),
	price_hint: z.string().nullable().optional(),
	is_promoted: z.boolean(),
	// Defaulted rather than required so a portal build newer than the API it
	// talks to keeps rendering instead of failing the whole list parse. Public
	// and not-editable is what every event was before visibility shipped.
	visibility: eventVisibilitySchema.default("public"),
	invite_status: inviteStatusSchema.nullable().default(null),
	can_edit: z.boolean().default(false),
	attendee_count: z.number().int(),
	// An event nobody answered yet comes back as null from API builds that
	// predate the empty-array fix; read it as no attendees.
	attendees: z
		.array(eventAttendeeSchema)
		.nullish()
		.transform((list) => list ?? []),
	created_at: z.coerce.date(),
});

// One invitation to an event. user_id is absent for an address that is not a
// registered user yet; the organiser-only fields are absent for everyone else.
export const eventInviteeSchema = z.object({
	id: z.string(),
	event_id: z.string().uuid(),
	user_id: z.string().uuid().nullish(),
	display_name: z.string(),
	avatar_url: z.string().nullish(),
	status: inviteStatusSchema,
	email: z.string().nullish(),
	invited_by: z.string().uuid().nullish(),
	invited_at: z.coerce.date().nullish(),
	responded_at: z.coerce.date().nullish(),
});

export const addEventInviteesSchema = z.object({
	user_ids: z.array(z.string().uuid()).min(1).max(100),
});

// The slices of an event's people a push can address. The client names slices,
// never people: the server resolves who is in them.
export const eventMessageAudienceSchema = z.enum(["going", "maybe", "invited"]);

export const EVENT_MESSAGE_MAX_LENGTH = 200;

export const sendEventMessageSchema = z.object({
	audiences: z.array(eventMessageAudienceSchema).min(1),
	message: z.string().trim().min(1).max(EVENT_MESSAGE_MAX_LENGTH),
});

export const eventMessageReceiptSchema = z.object({
	recipient_count: z.number().int(),
});

export const recurrenceFrequencySchema = z.enum(["weekly", "biweekly", "monthly"]);

export const createEventSchema = z
	.object({
		title: z.string().min(1, "Title is required"),
		description: z.string().nullable().optional(),
		event_type: eventTypeSchema,
		start_time: z.date(),
		end_time: z.date(),
		location: z.string().nullable().optional(),
		max_attendees: z.number().int().min(1).nullable().optional(),
		is_recurring: z.boolean().optional(),
		recurrence_frequency: recurrenceFrequencySchema.nullable().optional(),
		recurrence_end_date: z.date().nullable().optional(),
		price_hint: z.string().nullable().optional(),
		is_promoted: z.boolean().optional(),
		visibility: eventVisibilitySchema.optional(),
	})
	.refine((data) => data.end_time > data.start_time, {
		message: "End must be after start.",
		path: ["end_time"],
	});

export const updateEventSchema = z
	.object({
		title: z.string().min(1).optional(),
		description: z.string().nullable().optional(),
		start_time: z.date().optional(),
		end_time: z.date().optional(),
		location: z.string().nullable().optional(),
		max_attendees: z.number().int().min(1).nullable().optional(),
		status: eventStatusSchema.optional(),
		price_hint: z.string().nullable().optional(),
		is_promoted: z.boolean().optional(),
		visibility: eventVisibilitySchema.optional(),
	})
	.refine(
		(data) =>
			data.start_time === undefined ||
			data.end_time === undefined ||
			data.end_time > data.start_time,
		{ message: "End must be after start.", path: ["end_time"] },
	);

export type EventType = z.infer<typeof eventTypeSchema>;
export type EventStatus = z.infer<typeof eventStatusSchema>;
export type AttendeeStatus = z.infer<typeof attendeeStatusSchema>;
export type EventVisibility = z.infer<typeof eventVisibilitySchema>;
export type InviteStatus = z.infer<typeof inviteStatusSchema>;
export type EventAttendee = z.infer<typeof eventAttendeeSchema>;
export type ClubEvent = z.infer<typeof eventSchema>;
export type EventInvitee = z.infer<typeof eventInviteeSchema>;
export type AddEventInvitees = z.infer<typeof addEventInviteesSchema>;
export type EventMessageAudience = z.infer<typeof eventMessageAudienceSchema>;
export type SendEventMessage = z.infer<typeof sendEventMessageSchema>;
export type EventMessageReceipt = z.infer<typeof eventMessageReceiptSchema>;
export type EventResponse = z.infer<typeof eventSchema>;
export type RecurrenceFrequency = z.infer<typeof recurrenceFrequencySchema>;
export type CreateEvent = z.infer<typeof createEventSchema>;
export type UpdateEvent = z.infer<typeof updateEventSchema>;
